import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Container,
  Divider,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupsIcon from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import CalculateIcon from '@mui/icons-material/Calculate';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SchoolIcon from '@mui/icons-material/School';

import SectionCard from './Common/SectionCard';
import apiAxiosInstance from '../utils/axiosInstance';
import { useTeacher } from '../context/TeacherContext';
import { hasReading, hasMath } from '../utils/subjectHelpers';

/**
 * Dashboard component
 * 
 * Main dashboard with simple entry points to core features.
 * Straightforward layout for easy teacher adoption.
 */
const Dashboard: React.FC = () => {
  const { teacher } = useTeacher();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  // Check teacher subjects for conditional rendering
  const teacherHasReading = hasReading(teacher);
  const teacherHasMath = hasMath(teacher);
  // If teacher has no subjects configured, show both by default
  const showBothByDefault = !teacherHasReading && !teacherHasMath;

  const handleOpenFeedback = () => setFeedbackOpen(true);
  const handleCloseFeedback = () => {
    if (!isSubmittingFeedback) {
      setFeedbackOpen(false);
      if (!feedbackSubmitted) {
        setFeedback('');
        setFeedbackError(null);
      }
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      return;
    }

    setIsSubmittingFeedback(true);
    setFeedbackError(null);

    try {
      await apiAxiosInstance.post('/api/feedback', {
        page: 'dashboard-start',
        rating: 0,
        comment: feedback,
        timestamp: new Date().toISOString(),
      });

      setFeedbackSubmitted(true);
      setFeedback('');

      setTimeout(() => {
        setFeedbackSubmitted(false);
        setFeedbackOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedbackError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box p={2}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            What would you like to do?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Share feedback">
              <IconButton color="primary" onClick={handleOpenFeedback}>
                <FeedbackOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Primary Entry Points */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper
              component={Link}
              to="/edu-chat"
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.02))',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PsychologyIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Teaching Chat
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get teaching ideas, plan lessons, and create materials with AI assistance
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Reading/ELA: Generate a Passage - shows for Reading/ELA teachers or if no subjects configured */}
          {(teacherHasReading || showBothByDefault) && (
            <Grid item xs={12} md={6}>
              <Paper
                component={Link}
                to="/assessments"
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08), rgba(76, 175, 80, 0.02))',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'success.main',
                    boxShadow: '0 4px 20px rgba(76, 175, 80, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ArticleIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Generate a Passage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create passages, question sets, and answer keys in minutes
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Math: Generate Word Problems - shows for Math teachers or if no subjects configured */}
          {(teacherHasMath || showBothByDefault) && (
            <Grid item xs={12} md={6}>
              <Paper
                component={Link}
                to="/math-word-problems"
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.08), rgba(156, 39, 176, 0.02))',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'secondary.main',
                    boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CalculateIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Math Word Problems
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generate math word problems aligned to standards
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Targeted Instruction */}
          <Grid item xs={12} md={6}>
            <Paper
              component={Link}
              to="/instruction"
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08), rgba(33, 150, 243, 0.02))',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'info.main',
                  boxShadow: '0 4px 20px rgba(33, 150, 243, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: 'info.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <SchoolIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Targeted Instruction
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Plan small group lessons and individual interventions with AI coaching
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Behavior Coach */}
          <Grid item xs={12} md={6}>
            <Paper
              component={Link}
              to="/behavior"
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.08), rgba(255, 152, 0, 0.02))',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'warning.main',
                  boxShadow: '0 4px 20px rgba(255, 152, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: 'warning.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <SupportAgentIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Behavior Coach
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track student behavior, get coaching advice, and manage intervention strategies
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button variant="outlined" size="small" component={Link} to="/small-group/lesson-plan">
              Create Lesson Plan
            </Button>
            <Button variant="outlined" size="small" component={Link} to="/tests/create">
              Create Test
            </Button>
            <Button variant="outlined" size="small" component={Link} to="/passage-list">
              View Passages
            </Button>
            {(teacherHasMath || showBothByDefault) && (
              <Button variant="outlined" size="small" component={Link} to="/math-word-problems-list">
                View Math Problems
              </Button>
            )}
            <Button variant="outlined" size="small" component={Link} to="/small-group/saved">
              Saved Lessons
            </Button>
            <Button variant="outlined" size="small" component={Link} to="/instruction">
              Targeted Instruction
            </Button>
          </Box>
        </Box>

      </Box>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackOpen}
        onClose={handleCloseFeedback}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Your Feedback</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Let us know what works well or what you want to see improved.
          </Typography>
          {feedbackSubmitted ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Thank you for your feedback!
            </Alert>
          ) : (
            <>
              <TextField
                multiline
                rows={5}
                fullWidth
                placeholder="Share your thoughts..."
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                disabled={isSubmittingFeedback}
                autoFocus
                sx={{ mb: 2 }}
              />
              {feedbackError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {feedbackError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        {!feedbackSubmitted && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseFeedback} disabled={isSubmittingFeedback}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitFeedback}
              disabled={!feedback.trim() || isSubmittingFeedback}
              endIcon={
                isSubmittingFeedback ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
            >
              {isSubmittingFeedback ? 'Sending...' : 'Send Feedback'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default Dashboard;
